import { useDroppable } from "@dnd-kit/core";
import DealCard from "./DealCard";
import { cn } from "@/utils/common.utils";
import type { Deal } from "@/types/deals.types";
import type { BOARD_COLUMNS } from "@/constant/deals.constant";

interface DealColumnProps {
  column: (typeof BOARD_COLUMNS)[number];
  deals: Deal[];
  /** Whether cards in this column can be picked up, and whether the column
   *  accepts a drop. Both hang off the same permission, so one flag. */
  draggable?: boolean;
}

export default function DealColumn({ column, deals, draggable = false }: DealColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: !draggable });

  return (
    <section className="min-w-0" data-testid={`phase-column-${column.id}`}>
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-xs font-semibold text-dash-muted">{column.label}</h2>
        <span className="flex size-[19px] shrink-0 items-center justify-center rounded-full bg-dash-chip text-[10px] font-semibold text-dash-muted">
          {deals.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-3 rounded-xl transition-colors",
          // The whole column is the drop target, not the gap between cards —
          // there is no ordering within a column, only which column you're in.
          isOver && "bg-dash-chip/60 outline-dashed outline-2 outline-offset-2 outline-dash-greenDeep"
        )}
        data-testid={`phase-dropzone-${column.id}`}
      >
        {deals.map((deal) => <DealCard key={deal.id} deal={deal} draggable={draggable} />)}
        {/* Kept mounted while a card is over an empty column so there's
            something with height to drop onto. */}
        {deals.length === 0 && (
          <div className="flex min-h-24 items-center justify-center rounded-xl border border-dash-border text-xs text-dash-placeholder">
            {isOver ? "Move here" : "No deals"}
          </div>
        )}
      </div>
    </section>
  );
}
